import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, IconControl, Loading, SearchInput, TabPaneControl } from "spark-core-dx/components";
import { useCoreContext } from "spark-core-dx/contexts";
import { useOperationDialog } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { Operations } from "../../../../services/ItemServices";
import AIPanel from "../../../../components/AIChat/AIPanel";
import CardGridControl from "../CardGridControl";
import { ResourceCatalogListControl, ResourceCatalogRow } from "../ResourceCatalogPanel";
import {
  asArray,
  buildResourceOperationQueryParams,
  getOperationItem,
  getOperationItems,
  readValue,
  requireSuccessfulServiceResponse
} from "../wizard/resourceConfigWizardUtils";
import {
  getDeploymentDisplayName,
  getDeploymentRecordId,
  getEntityObjectDisplayName,
  getEntityObjectRecordId
} from "../wizard/steps/destinationSetupStepUtils";
import ScheduleEditorControl from "./ScheduleEditorControl";
import {
  buildDestinationScheduleDraft,
  buildDestinationScheduleScopeKey,
  buildDestinationScheduleTimingSummary,
  CORE_SELECTDATA_TIMEZONES_OPERATION,
  CONVOKE_SELECTDATA_RUNTIME_CAPACITY_POOL_PROFILE_OPERATION,
  buildScheduleStepValues,
  DESTINATION_SCHEDULE_FORM_GROUP_ID,
  fieldMetadata,
  fieldValue,
  getDestinationRecordId,
  getScheduleRowKey,
  getScheduleScopeKey,
  isScheduleActive,
  normalizeGuid,
  normalizeStringArray,
  resolveScheduleDecision,
  SCHEDULE_DECISION_CONFIGURE,
  SCHEDULE_DECISION_NONE,
  validateDestinationSchedules
} from "./destinationScheduleControlUtils";
import MarketplaceRuntimeCapacityPurchaseDialog, { getRuntimeCapacityActionVerb } from "../../../marketplace/components/MarketplaceRuntimeCapacityPurchaseDialog";
import "./destinationScheduleControl.scss";

const scheduleOperations = Operations.DestinationScheduleItem || {};
const DESTINATION_SCHEDULE_ITEM_SERVICE = "DestinationSchedule";
const DETAIL_TAB_SCHEDULE = "schedule";
const DETAIL_TAB_EVENTS = "events";
const DETAIL_TABS = [
  { key: DETAIL_TAB_SCHEDULE, label: "Schedule" },
  { key: DETAIL_TAB_EVENTS, label: "Events" }
];
const ENTITY_OBJECT_SELECTOR_METADATA = {
  Name: "DestinationScheduleEntityObjectSelector",
  DisplayName: "Entity Objects",
  Fields: [
    { FieldName: "Name", DisplayName: "Name", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "Object", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "Status", DisplayName: "Status", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 }
  ]
};

const buildFilterTextBoxField = (fieldName, placeHolder, ariaLabel = placeHolder) => ({
  FieldName: fieldName,
  DisplayName: "",
  FieldType: SystemTypes.UIFieldType.TextBox,
  PlaceHolder: placeHolder,
  AriaLabel: ariaLabel
});

const normalizeResultMessage = (result, fallback) => {
  const operationResult = Array.isArray(result) ? result[0] : result;
  return readValue(operationResult, "DisplayMessage", "displayMessage", "Message", "message")
    || readValue(operationResult?.Message, "DisplayMessage", "displayMessage", "Content", "content")
    || fallback;
};

const operationFailed = (result) => {
  const operationResult = Array.isArray(result) ? result[0] : result;
  return readValue(operationResult, "Success", "success") === false
    || readValue(result, "Success", "success") === false;
};

const normalizeOperationKey = (value) => String(value || "").trim().toLowerCase();
const getOperationIdentity = (operation) =>
  readValue(operation, "Name", "OperationTypeId", "OpeationTypeId", "Value", "_id", "Id") || "";

const getFirstEntry = (value) => {
  if (!value) return null;
  if (typeof value.first === "function") return value.first();
  return asArray(value)[0] || null;
};

const getManagerSaveOperation = async (manager) => {
  if (!manager) return "";
  if (!manager.uiMetadata && typeof manager.LoadMetadata === "function") {
    await manager.LoadMetadata();
  }
  const metadata = getFirstEntry(manager.uiMetadata);
  return readValue(metadata, "SaveOperation", "saveOperation") || "";
};

const saveScheduleItem = async (manager, item) => {
  const saveOperation = await getManagerSaveOperation(manager);
  if (saveOperation && typeof manager?.RunOperation === "function") {
    return manager.RunOperation({
      operationName: saveOperation,
      id: item?._id,
      item,
      isItem: true,
      isWarning: true,
      supressAllErrors: true
    });
  }
  return manager.saveItem(item);
};

const option = (text, value) => ({ Text: text, Value: value });
const getPersistedScheduleId = (schedule) => readValue(schedule, "_id", "id") || "";
const normalizeScheduleForChangeCompare = (schedule) => {
  if (!schedule) return null;
  const normalized = { ...schedule };
  delete normalized._tempKey;
  return normalized;
};
const schedulesMatchForChangeCompare = (currentSchedule, savedSchedule) =>
  JSON.stringify(normalizeScheduleForChangeCompare(currentSchedule)) === JSON.stringify(normalizeScheduleForChangeCompare(savedSchedule));
const findSavedScheduleMatch = (savedSchedules, schedule) => {
  if (!schedule) return null;
  const persistedId = normalizeGuid(getPersistedScheduleId(schedule));
  if (persistedId) {
    const byId = asArray(savedSchedules).find((savedSchedule) => normalizeGuid(getPersistedScheduleId(savedSchedule)) === persistedId);
    if (byId) return byId;
  }
  const scopeKey = getScheduleScopeKey(schedule);
  return asArray(savedSchedules).find((savedSchedule) => getScheduleScopeKey(savedSchedule) === scopeKey) || null;
};
const dialogFailure = (message) => ({
  Success: false,
  Message: { DisplayMessage: message }
});
const getScheduleDeploymentKey = (schedule) => {
  const deploymentId = readValue(schedule, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId");
  if (deploymentId) return normalizeGuid(deploymentId);
  const scopeKey = String(getScheduleScopeKey(schedule) || "");
  const match = /^deployment:([^|]+)/i.exec(scopeKey);
  return normalizeGuid(match?.[1]);
};
const getDeploymentIdFromScopeKey = (scopeKey) => {
  const match = /^deployment:([^|]+)/i.exec(String(scopeKey || ""));
  return match?.[1] || "";
};
const getEntityObjectIdsFromScopeKey = (scopeKey) => {
  const entityScope = String(scopeKey || "").split("|entities:")[1] || "";
  if (!entityScope || normalizeGuid(entityScope) === "all") return [];
  return entityScope.split(",").map((entry) => entry.trim()).filter(Boolean);
};
const getScheduleScopeEntityKeys = (schedule) => {
  const explicitEntityIds = normalizeStringArray(readValue(schedule, "EntityObjectIds", "entityObjectIds"));
  if (explicitEntityIds.length) return explicitEntityIds.map(normalizeGuid).filter(Boolean);
  const scopeKey = String(getScheduleScopeKey(schedule) || "");
  const entityScope = scopeKey.split("|entities:")[1] || "";
  if (!entityScope || normalizeGuid(entityScope) === "all") return [];
  return entityScope.split(",").map(normalizeGuid).filter(Boolean);
};
const scheduleCoversAllEntities = (schedule) => {
  const scopeKey = String(getScheduleScopeKey(schedule) || "");
  const entityScope = scopeKey.split("|entities:")[1] || "";
  return normalizeGuid(entityScope) === "all";
};

const TIMEZONE_SELECTDATA_FIELD = fieldMetadata({
  fieldName: "TimeZoneId",
  displayName: "Timezone",
  fieldType: SystemTypes.UIFieldType.SearchSelectList,
  dataOperation: CORE_SELECTDATA_TIMEZONES_OPERATION,
  dataOperationApp: "Core",
  isRequired: true
});

const getSelectDataResultEnvelope = (selectDataResult) => {
  if (selectDataResult && typeof selectDataResult.first === "function") return selectDataResult.first();
  return Array.isArray(selectDataResult) ? selectDataResult[0] : selectDataResult;
};

const getSelectDataOptions = (selectDataResult) => {
  const result = getSelectDataResultEnvelope(selectDataResult);
  const wrappedOptions = asArray(readValue(result, "jsonData", "DataJson", "dataJson"));
  if (wrappedOptions.length) return wrappedOptions;

  const firstItem = asArray(readValue(result, "Items", "items"))[0];
  return asArray(readValue(firstItem, "TextValues", "textValues"));
};

const getTimezoneOptions = getSelectDataOptions;

const timezoneSelectDataFailed = (selectDataResult) => {
  const result = getSelectDataResultEnvelope(selectDataResult);
  return readValue(result, "error", "Error") === true
    || operationFailed(result);
};

const EVENT_STATUS_OPTIONS = [
  option("Created", "1"),
  option("In Progress", "2"),
  option("Completed", "3"),
  option("Failed", "0"),
  option("Cancelled", "4"),
  option("Scheduled", "5"),
  option("Paused", "6")
];
const ACTIVE_EVENT_STATUS_VALUES = ["1", "2"];
const EVENT_STATUS_LABELS = EVENT_STATUS_OPTIONS.reduce((labels, entry) => ({
  ...labels,
  [entry.Value]: entry.Text
}), {});
const SCHEDULE_MATERIALIZATION_STATUS_LABELS = {
  0: "None",
  1: "Previewed",
  2: "Scheduled",
  3: "Enabled",
  4: "Running",
  5: "Skipped",
  6: "Failed"
};
const EVENT_STATUS_VALUE_BY_NAME = {
  failed: "0",
  created: "1",
  inprogress: "2",
  running: "2",
  completed: "3",
  complete: "3",
  cancelled: "4",
  canceled: "4",
  scheduled: "5",
  paused: "6"
};

const formatScheduleMaterializationStatus = (value) => {
  const rawValue = readValue(value, "Value", "value", "Text", "text", "Name", "name") ?? value;
  const normalizedValue = String(rawValue ?? "").trim();
  if (!normalizedValue) return "";
  if (/^-?\d+$/.test(normalizedValue)) {
    return SCHEDULE_MATERIALIZATION_STATUS_LABELS[Number(normalizedValue)] || normalizedValue;
  }
  return normalizedValue;
};

const getScheduleEventSummary = (schedule) => {
  const status = formatScheduleMaterializationStatus(readValue(schedule, "LastMaterializationStatus", "lastMaterializationStatus"))
    || formatScheduleMaterializationStatus(readValue(schedule, "CurrentEventStatus", "currentEventStatus"));
  const message = readValue(schedule, "LastMaterializationMessage", "lastMaterializationMessage") || "";
  return { status, message };
};

const ScheduleEventSummary = ({ summary }) => {
  if (!summary.status && !summary.message) return null;

  return (
    <div className="destination-schedule-control__event-summary">
      <span>Status:</span>
      <strong>{summary.status || "Unknown"}</strong>
      {summary.message ? (
        <>
          <span aria-hidden="true">/</span>
          <span>{summary.message}</span>
        </>
      ) : null}
    </div>
  );
};

const unwrapMappedValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return readValue(value, "Value", "value", "DisplayValue", "displayValue", "Text", "text", "Name", "name") ?? value;
  }
  return value;
};
const hasEventValue = (value) => value !== undefined && value !== null && value !== "";

const getMappedValue = (source, ...keys) => {
  if (!source) return "";
  const keySet = keys.map((key) => String(key || "").toLowerCase());
  if (Array.isArray(source)) {
    const match = source.find((entry) => keySet.includes(String(readValue(entry, "Name", "name", "Key", "key", "FieldName", "fieldName") || "").toLowerCase()));
    return unwrapMappedValue(match ? readValue(match, "Value", "value", "DisplayValue", "displayValue") : "");
  }
  if (typeof source !== "object") return "";
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) return unwrapMappedValue(source[key]);
    const lowerKey = String(key || "").charAt(0).toLowerCase() + String(key || "").slice(1);
    if (Object.prototype.hasOwnProperty.call(source, lowerKey)) return unwrapMappedValue(source[lowerKey]);
  }
  return "";
};

const getEventMappedField = (event, ...keys) => {
  const directValue = readValue(event, ...keys);
  if (hasEventValue(directValue)) return directValue;
  const customValue = getMappedValue(readValue(event, "CustomFields", "customFields"), ...keys);
  if (hasEventValue(customValue)) return customValue;
  const metadataValue = getMappedValue(readValue(event, "Metadata", "metadata"), ...keys);
  if (hasEventValue(metadataValue)) return metadataValue;
  return getMappedValue(readValue(event, "Data", "data"), ...keys);
};

const getEventDestinationScheduleId = (event) =>
  getEventMappedField(event, "DestinationScheduleId", "destinationScheduleId");

const getEventId = (event) =>
  readValue(event, "_id", "id", "UniqueId", "uniqueId", "EventId", "eventId") || "";

const normalizeEventStatusValue = (rawValue) => {
  const value = unwrapMappedValue(rawValue);
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";
  if (/^-?\d+$/.test(normalized)) return normalized;
  return EVENT_STATUS_VALUE_BY_NAME[normalized.replace(/[\s_-]/g, "").toLowerCase()] || normalized;
};

const getEventStatusValue = (event) =>
  normalizeEventStatusValue(getEventMappedField(event, "EventStatusType", "eventStatusType", "EventStatus", "eventStatus", "Status", "status", "State", "state"));

const getEventStatusLabel = (event) => {
  const statusValue = getEventStatusValue(event);
  return EVENT_STATUS_LABELS[statusValue] || String(statusValue || "Unknown");
};

const getEventTitle = (event) =>
  readValue(event, "DisplayName", "displayName", "Name", "name", "EventName", "eventName", "OperationName", "operationName", "ProcessorType", "processorType")
  || getEventId(event)
  || "Scheduled event";

const getEventMessage = (event) =>
  readValue(event, "DisplayMessage", "displayMessage", "Message", "message", "ErrorMessage", "errorMessage", "StatusMessage", "statusMessage")
  || "";

const getEventDate = (event) =>
  readValue(event, "CreatedDate", "createdDate", "CreateDate", "createDate", "StartDate", "startDate", "DateCreated", "dateCreated") || "";

const formatEventDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const eventMatchesSearch = (event, search) => {
  const normalizedSearch = String(search || "").trim().toLowerCase();
  if (!normalizedSearch) return true;
  return [
    getEventTitle(event),
    getEventMessage(event),
    getEventStatusLabel(event),
    getEventId(event),
    getEventDate(event)
  ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
};

const buildScheduleEventFilterNode = (fieldName, value) => value ? ({
  Variable: "x",
  Name: fieldName,
  Method: "==",
  Value: value,
  IsConjunction: false
}) : null;

const combineScheduleEventFilterNodes = (left, right) => {
  if (!left) return right || null;
  if (!right) return left;
  return {
    Variable: "x",
    Method: "&&",
    IsConjunction: true,
    Left: left,
    Right: right
  };
};

const buildScheduleEventQuery = ({ clientResourceId, resourceConfigurationId }) => {
  const rootFilterNode = combineScheduleEventFilterNodes(
    buildScheduleEventFilterNode("ClientResourceId", clientResourceId),
    buildScheduleEventFilterNode("ResourceConfigurationId", resourceConfigurationId)
  );
  const model = {
    RootFilterNode: rootFilterNode,
    Skip: 0,
    Take: 100,
    SortBy: "CreatedDate",
    SortOrder: "DESC"
  };
  return {
    model,
    skip: 0,
    take: 100,
    sortBy: "CreatedDate",
    sortOrder: "DESC"
  };
};

const DestinationScheduleStatusSection = ({
  schedule,
  clientResourceId,
  resourceConfigurationId,
  eventManager
}) => {
  const scheduleId = getPersistedScheduleId(schedule);
  const eventSummary = useMemo(() => getScheduleEventSummary(schedule), [schedule]);
  const [eventSearch, setEventSearch] = useState("");
  const [eventStatusValues, setEventStatusValues] = useState(ACTIVE_EVENT_STATUS_VALUES);
  const [eventState, setEventState] = useState({
    loading: Boolean(scheduleId && eventManager?.Query),
    error: "",
    events: []
  });

  useEffect(() => {
    setEventSearch("");
    setEventStatusValues(ACTIVE_EVENT_STATUS_VALUES);
  }, [scheduleId]);

  useEffect(() => {
    let mounted = true;
    const normalizedScheduleId = normalizeGuid(scheduleId);
    if (!normalizedScheduleId || !eventManager?.Query) {
      setEventState({ loading: false, error: "", events: [] });
      return undefined;
    }
    setEventState((previous) => ({ ...previous, loading: true, error: "" }));

    const loadEvents = async () => {
      try {
        const result = await eventManager.Query(buildScheduleEventQuery({ clientResourceId, resourceConfigurationId }));
        if (!mounted) return;
        requireSuccessfulServiceResponse(result, "Schedule events could not be loaded.");
        const events = getOperationItems(result).filter((event) =>
          normalizeGuid(getEventDestinationScheduleId(event)) === normalizedScheduleId
        );
        setEventState({ loading: false, error: "", events });
      } catch (ex) {
        if (mounted) {
          setEventState({
            loading: false,
            error: ex?.message || "Schedule events could not be loaded.",
            events: []
          });
        }
      }
    };

    void loadEvents();
    return () => {
      mounted = false;
    };
  }, [clientResourceId, eventManager, resourceConfigurationId, scheduleId]);

  const filteredEvents = useMemo(() => {
    const selectedStatuses = new Set(normalizeStringArray(eventStatusValues).map(String));
    return eventState.events.filter((event) => {
      const statusMatches = selectedStatuses.size === 0 || selectedStatuses.has(getEventStatusValue(event));
      return statusMatches && eventMatchesSearch(event, eventSearch);
    });
  }, [eventSearch, eventState.events, eventStatusValues]);

  const emptyEventMessage = scheduleId
    ? "No active created or in-progress events are attached to this schedule."
    : "Save this schedule before event history can be displayed.";

  return (
    <div className="destination-schedule-control__section destination-schedule-control__status-section">
      <div className="destination-schedule-control__section-heading">
        <h4>Events</h4>
        <span>{filteredEvents.length} shown</span>
      </div>
      {eventSummary.status || eventSummary.message ? (
        <ScheduleEventSummary summary={eventSummary} />
      ) : (
        <div className="destination-schedule-control__empty-note">No schedule event status has been recorded yet.</div>
      )}
      <div className="destination-schedule-control__event-filters">
        <SearchInput
          className="destination-schedule-control__event-search"
          placeholder="Search schedule events..."
          value={eventSearch}
          disabled={eventState.loading}
          onChange={setEventSearch}
        />
        <FieldControl
          className="destination-schedule-control__field-control destination-schedule-control__event-status-filter"
          fieldMetadata={{
            ...fieldMetadata({
              fieldName: "ScheduleEventStatusFilter",
              displayName: "",
              fieldType: SystemTypes.UIFieldType.MultiSelectList,
              options: EVENT_STATUS_OPTIONS
            }),
            AriaLabel: "Event Status"
          }}
          selectData={EVENT_STATUS_OPTIONS}
          value={eventStatusValues}
          disabled={eventState.loading}
          groupId={`${DESTINATION_SCHEDULE_FORM_GROUP_ID}-event-status`}
          onChange={(nextValue) => setEventStatusValues(normalizeStringArray(nextValue))}
          disableError
        />
      </div>
      <div className="destination-schedule-control__event-list" role="list" aria-label="Selected schedule events">
        {eventState.loading ? (
          <div className="destination-schedule-control__event-loading"><Loading /></div>
        ) : eventState.error ? (
          <div className="resource-wizard__inline-error">{eventState.error}</div>
        ) : filteredEvents.length ? (
          filteredEvents.map((event) => {
            const eventId = getEventId(event);
            const eventDate = formatEventDate(getEventDate(event));
            const eventMessage = getEventMessage(event);
            return (
              <article className="destination-schedule-control__event-row" key={eventId || `${getEventTitle(event)}-${eventDate}`} role="listitem">
                <div className="destination-schedule-control__event-main">
                  <strong>{getEventTitle(event)}</strong>
                  {eventMessage ? <span>{eventMessage}</span> : null}
                </div>
                <span className="destination-schedule-control__event-status">{getEventStatusLabel(event)}</span>
                <dl className="destination-schedule-control__event-meta">
                  {eventId ? (
                    <div>
                      <dt>Event</dt>
                      <dd>{eventId}</dd>
                    </div>
                  ) : null}
                  {eventDate ? (
                    <div>
                      <dt>Created</dt>
                      <dd>{eventDate}</dd>
                    </div>
                  ) : null}
                </dl>
              </article>
            );
          })
        ) : (
          <div className="destination-schedule-control__empty-note">
            {eventState.events.length ? "No selected schedule events match the current filters." : emptyEventMessage}
          </div>
        )}
      </div>
    </div>
  );
};

const DestinationScheduleControl = ({
  mode = "setup",
  destination,
  deployments = [],
  entityObjects = [],
  entityDeployments = [],
  defaults = {},
  clientResourceId = "",
  resourceConfigurationId = "",
  environmentKey = "",
  clientProductOffers = [],
  isSelfHostedResourceMode = false,
  scheduleManager,
  eventManager,
  initialDeploymentId = "",
  initialEntityObjectIds = [],
  initialScopeKey = "",
  readOnly = false,
  saving = false,
  onDirty,
  onApiReady,
  onSetupSave,
  onSetupContinue,
  onSetupNoSchedule,
  onSaved,
  onManagementActionsChange
}) => {
  const coreContext = useCoreContext();
  const scheduleOperationDialog = useOperationDialog(DESTINATION_SCHEDULE_ITEM_SERVICE);
  const destinationId = getDestinationRecordId(destination);
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleKey, setSelectedScheduleKey] = useState("");
  const [selectedDeploymentId, setSelectedDeploymentId] = useState("");
  const [selectedEntityObjectIds, setSelectedEntityObjectIds] = useState([]);
  const [loading, setLoading] = useState(Boolean(destinationId && resourceConfigurationId));
  const [localError, setLocalError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [runtimeCapacityPoolProfileOptions, setRuntimeCapacityPoolProfileOptions] = useState([]);
  const [addScheduleDialogOpen, setAddScheduleDialogOpen] = useState(false);
  const [removeScheduleKey, setRemoveScheduleKey] = useState("");
  const [editingScheduleKey, setEditingScheduleKey] = useState("");
  const [runtimeCapacityPurchaseOpen, setRuntimeCapacityPurchaseOpen] = useState(false);
  const [detailTab, setDetailTab] = useState(DETAIL_TAB_SCHEDULE);
  const [scheduleSearch, setScheduleSearch] = useState("");
  const loadKeyRef = useRef("");
  const schedulesRef = useRef(schedules);
  const savedSchedulesRef = useRef([]);
  const lastSavedSelectedScheduleRef = useRef(null);
  const editOriginalScheduleRef = useRef(null);
  const modeRef = useRef(mode);
  const scheduleManagerRef = useRef(scheduleManager);
  const timezoneSelectDataRequestedRef = useRef(false);
  const timezoneSelectDataErrorRef = useRef("");
  const initialFilterAppliedRef = useRef("");
  const initialScopeAppliedRef = useRef("");
  const managementMode = mode === "management";

  schedulesRef.current = schedules;
  modeRef.current = mode;
  scheduleManagerRef.current = scheduleManager;

  const deploymentOptions = useMemo(() => asArray(deployments)
    .filter((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
    .map((deployment) => option(getDeploymentDisplayName(deployment, defaults), getDeploymentRecordId(deployment)))
    .filter((entry) => entry.Value), [defaults, deployments]);
  const deploymentFilterOptions = useMemo(() => [
    option("All Deployments", ""),
    ...deploymentOptions
  ], [deploymentOptions]);
  const deploymentDisplayById = useMemo(() => new Map(deploymentOptions.map((entry) => [
    normalizeGuid(entry.Value),
    entry.Text
  ])), [deploymentOptions]);
  const initialFilterDeploymentId = initialDeploymentId || getDeploymentIdFromScopeKey(initialScopeKey);
  const initialFilterEntityObjectIds = useMemo(() => {
    const explicitIds = normalizeStringArray(initialEntityObjectIds);
    return explicitIds.length ? explicitIds : getEntityObjectIdsFromScopeKey(initialScopeKey);
  }, [initialEntityObjectIds, initialScopeKey]);
  const linkedSpecificEntityScope = managementMode && initialFilterEntityObjectIds.length > 0;
  const linkedScopeDeploymentId = initialFilterDeploymentId;
  const initialFilterSignature = [
    initialFilterDeploymentId,
    initialFilterEntityObjectIds.join(","),
    initialScopeKey
  ].join("|");

  const getScheduleOperationDefinition = useCallback((operationName) => {
    const operationKey = normalizeOperationKey(operationName);
    return asArray(coreContext?.Operations).find((operation) =>
      normalizeOperationKey(getOperationIdentity(operation)) === operationKey
    ) || null;
  }, [coreContext?.Operations]);

  const scheduledScope = useMemo(() => {
    const selectedDeploymentKey = normalizeGuid(selectedDeploymentId);
    const scheduledEntityIds = new Set();
    let allEntitiesScheduled = false;
    let hasScheduledScopes = false;
    asArray(schedules).filter(isScheduleActive).forEach((schedule) => {
      if (getScheduleDeploymentKey(schedule) !== selectedDeploymentKey) return;
      hasScheduledScopes = true;
      if (scheduleCoversAllEntities(schedule)) {
        allEntitiesScheduled = true;
        return;
      }
      getScheduleScopeEntityKeys(schedule).forEach((entityId) => scheduledEntityIds.add(entityId));
    });
    return { allEntitiesScheduled, hasScheduledScopes, scheduledEntityIds };
  }, [schedules, selectedDeploymentId]);

  const entityOptions = useMemo(() => {
    const selectedDeploymentKey = normalizeGuid(selectedDeploymentId);
    if (!selectedDeploymentKey) return [];
    const deployedEntityIds = new Set(asArray(entityDeployments)
      .filter((row) => normalizeGuid(readValue(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId")) === selectedDeploymentKey)
      .map((row) => normalizeGuid(readValue(row, "EntityObjectId", "entityObjectId")))
      .filter(Boolean));
    const restrictToDeployment = deployedEntityIds.size > 0;
    if (scheduledScope.allEntitiesScheduled) return [];
    return asArray(entityObjects)
      .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
      .filter((entityObject) => !restrictToDeployment || deployedEntityIds.has(normalizeGuid(getEntityObjectRecordId(entityObject))))
      .filter((entityObject) => !scheduledScope.scheduledEntityIds.has(normalizeGuid(getEntityObjectRecordId(entityObject))))
      .map((entityObject) => option(getEntityObjectDisplayName(entityObject), getEntityObjectRecordId(entityObject)))
      .filter((entry) => entry.Value);
  }, [entityDeployments, entityObjects, scheduledScope, selectedDeploymentId]);
  const entityObjectById = useMemo(() => {
    const next = new Map();
    asArray(entityObjects).forEach((entityObject) => {
      const entityObjectId = getEntityObjectRecordId(entityObject);
      if (entityObjectId) next.set(normalizeGuid(entityObjectId), entityObject);
    });
    return next;
  }, [entityObjects]);
  const addDialogEntityRows = useMemo(() => {
    const selectableKeys = new Set(entityOptions.map((entry) => normalizeGuid(entry.Value)));
    const lockedKeys = new Set(initialFilterEntityObjectIds.map(normalizeGuid).filter(Boolean));
    const rows = linkedSpecificEntityScope
      ? initialFilterEntityObjectIds.map((entityObjectId) => entityObjectById.get(normalizeGuid(entityObjectId))).filter(Boolean)
      : asArray(entityObjects).filter((entityObject) => selectableKeys.has(normalizeGuid(getEntityObjectRecordId(entityObject))));
    return rows.map((entityObject) => {
      const entityObjectId = getEntityObjectRecordId(entityObject);
      return {
        _id: entityObjectId,
        Name: getEntityObjectDisplayName(entityObject),
        Object: readValue(entityObject, "ObjectName", "objectName", "InternalName", "internalName", "Name", "name") || entityObjectId,
        Status: linkedSpecificEntityScope && lockedKeys.has(normalizeGuid(entityObjectId)) ? "Linked scope" : "Available",
        __entityObject: entityObject
      };
    });
  }, [entityObjectById, entityObjects, entityOptions, initialFilterEntityObjectIds, linkedSpecificEntityScope]);

  useEffect(() => {
    if (!deploymentOptions.length || initialFilterAppliedRef.current === initialFilterSignature) return;
    const requestedDeploymentKey = normalizeGuid(initialFilterDeploymentId);
    if (!requestedDeploymentKey && !initialFilterEntityObjectIds.length && !initialScopeKey) return;

    const requestedDeployment = deploymentOptions.find((entry) => normalizeGuid(entry.Value) === requestedDeploymentKey);
    if (!requestedDeployment) return;

    initialFilterAppliedRef.current = initialFilterSignature;
    setSelectedDeploymentId(requestedDeployment.Value);
    setSelectedEntityObjectIds(initialFilterEntityObjectIds);
    setSelectedScheduleKey("");
    setEditingScheduleKey("");
    editOriginalScheduleRef.current = null;
  }, [deploymentOptions, initialFilterDeploymentId, initialFilterEntityObjectIds, initialFilterSignature, initialScopeKey]);

  useEffect(() => {
    const requestedDeploymentKey = normalizeGuid(initialFilterDeploymentId);
    const requestedDeploymentAvailable = requestedDeploymentKey
      && deploymentOptions.some((entry) => normalizeGuid(entry.Value) === requestedDeploymentKey);
    if (!managementMode && !selectedDeploymentId && deploymentOptions.length && !requestedDeploymentAvailable) {
      setSelectedDeploymentId(deploymentOptions[0].Value);
    }
  }, [deploymentOptions, initialFilterDeploymentId, managementMode, selectedDeploymentId]);

  useEffect(() => {
    const availableEntityIds = new Set(entityOptions.map((entry) => normalizeGuid(entry.Value)));
    setSelectedEntityObjectIds((previous) => {
      if (linkedSpecificEntityScope) {
        const lockedIds = initialFilterEntityObjectIds.filter(Boolean);
        if (previous.length === lockedIds.length && previous.every((entry, index) => entry === lockedIds[index])) return previous;
        return lockedIds;
      }
      const next = previous.filter((entityObjectId) => availableEntityIds.has(normalizeGuid(entityObjectId)));
      if (next.length === previous.length && next.every((entry, index) => entry === previous[index])) return previous;
      return next;
    });
  }, [entityOptions, initialFilterEntityObjectIds, linkedSpecificEntityScope]);

  useEffect(() => {
    let mounted = true;
    const activeManager = scheduleManagerRef.current;
    if (timezoneSelectDataRequestedRef.current) return undefined;
    if (!activeManager?.GetSelectData) {
      if (!activeManager || Object.keys(activeManager).length === 0) return undefined;
      const message = "Destination Schedule timezone select data service is not available.";
      timezoneSelectDataErrorRef.current = message;
      setTimezoneOptions([]);
      setLocalError(message);
      return undefined;
    }
    timezoneSelectDataRequestedRef.current = true;

    const load = async () => {
      try {
        const result = await activeManager.GetSelectData(TIMEZONE_SELECTDATA_FIELD);
        if (timezoneSelectDataFailed(result)) {
          throw new Error(normalizeResultMessage(result, "Unable to load timezone select data."));
        }
        const options = getTimezoneOptions(result);
        if (!options.length) {
          throw new Error(`${CORE_SELECTDATA_TIMEZONES_OPERATION} returned no timezone options.`);
        }
        if (mounted) {
          setTimezoneOptions(options);
          const previousTimezoneError = timezoneSelectDataErrorRef.current;
          timezoneSelectDataErrorRef.current = "";
          if (previousTimezoneError) {
            setLocalError((current) => (current === previousTimezoneError ? "" : current));
          }
        }
      } catch (error) {
        timezoneSelectDataRequestedRef.current = false;
        const message = error?.message || "Unable to load timezone select data.";
        timezoneSelectDataErrorRef.current = message;
        if (mounted) {
          setTimezoneOptions([]);
          setLocalError(message);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [scheduleManager]);

  useEffect(() => {
    let mounted = true;
    const activeManager = scheduleManagerRef.current;
    const loadKey = `${resourceConfigurationId || "none"}:${destinationId || "none"}`;
    if (!resourceConfigurationId || !destinationId) {
      setSchedules([]);
      setSelectedScheduleKey("");
      setLoading(false);
      return undefined;
    }
    if (!activeManager?.Query) {
      setLoading(false);
      setLocalError("Destination Schedule service is not available.");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        const result = await activeManager.Query({
          model: "x => x.IsDeleted == false && x.ResourceConfigurationId == resourceConfigurationId",
          params: { resourceConfigurationId }
        });
        if (!mounted) return;
        requireSuccessfulServiceResponse(result, "Destination Schedules could not be loaded.");
        const normalizedDestinationId = normalizeGuid(destinationId);
        const loadedSchedules = getOperationItems(result)
          .filter((schedule) => normalizeGuid(readValue(schedule, "SchemaDestinationId", "schemaDestinationId")) === normalizedDestinationId)
          .sort((left, right) => String(readValue(left, "Name", "name") || "").localeCompare(String(readValue(right, "Name", "name") || "")));
        savedSchedulesRef.current = loadedSchedules;
        setSchedules(loadedSchedules);
        setSelectedScheduleKey((previous) => previous || getScheduleRowKey(loadedSchedules[0], 0));
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || "Destination Schedules could not be loaded.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [destinationId, resourceConfigurationId]);

  const visibleScheduleEntries = useMemo(() => {
    const selectedDeploymentKey = normalizeGuid(selectedDeploymentId);
    return schedules
      .map((schedule, index) => ({
        schedule,
        index,
        key: getScheduleRowKey(schedule, index)
      }))
      .filter(({ schedule }) => readValue(schedule, "IsDeleted", "isDeleted") !== true)
      .filter(({ schedule }) => !selectedDeploymentKey || getScheduleDeploymentKey(schedule) === selectedDeploymentKey);
  }, [schedules, selectedDeploymentId]);
  const visibleSchedules = useMemo(() => visibleScheduleEntries.map((entry) => entry.schedule), [visibleScheduleEntries]);
  const selectedScheduleEntry = useMemo(
    () => visibleScheduleEntries.find((entry) => entry.key === selectedScheduleKey) || null,
    [selectedScheduleKey, visibleScheduleEntries]
  );
  const selectedSchedule = selectedScheduleEntry?.schedule || null;
  const selectedSchedulePersistedId = getPersistedScheduleId(selectedSchedule);
  const selectedSavedSchedule = findSavedScheduleMatch(savedSchedulesRef.current, selectedSchedule);
  const selectedScheduleHasUnsavedChanges = Boolean(selectedSchedulePersistedId)
    && (!selectedSavedSchedule || !schedulesMatchForChangeCompare(selectedSchedule, selectedSavedSchedule));
  const selectedRuntimeCapacityGrantValue = readValue(selectedSchedule, "RuntimeCapacityClientProductOfferId", "runtimeCapacityClientProductOfferId") || "";
  const runtimeCapacityActionVerb = getRuntimeCapacityActionVerb({
    clientProductOffers,
    hasSelectedGrant: Boolean(selectedRuntimeCapacityGrantValue),
    fallbackActionVerb: "Add"
  });
  const selectedScheduleBlocksAdd = managementMode && Boolean(selectedSchedule) && !selectedSchedulePersistedId;
  const addRequiresEntitySelection = scheduledScope.hasScheduledScopes && selectedEntityObjectIds.length === 0;

  useEffect(() => {
    setDetailTab(DETAIL_TAB_SCHEDULE);
  }, [selectedScheduleKey]);

  useEffect(() => {
    let mounted = true;
    const selectedGrant = String(selectedRuntimeCapacityGrantValue || "").trim();
    if (!selectedGrant) {
      setRuntimeCapacityPoolProfileOptions([]);
      return undefined;
    }

    const activeManager = scheduleManagerRef.current;
    if (!activeManager?.RunOperation) {
      if (activeManager && Object.keys(activeManager).length > 0) {
        setRuntimeCapacityPoolProfileOptions([]);
      }
      return undefined;
    }

    const load = async () => {
      try {
        const result = await activeManager.RunOperation({
          operationName: CONVOKE_SELECTDATA_RUNTIME_CAPACITY_POOL_PROFILE_OPERATION,
          queryParams: {
            fv: selectedGrant,
            runtimeCapacityGrant: selectedGrant,
            RuntimeCapacityClientProductOfferId: selectedGrant,
            env: environmentKey || ""
          },
          isWarning: true,
          supressAllErrors: true
        });
        if (timezoneSelectDataFailed(result)) {
          throw new Error(normalizeResultMessage(result, "Unable to load runtime capacity pool profiles."));
        }
        const options = getSelectDataOptions(result);
        if (mounted) {
          setRuntimeCapacityPoolProfileOptions(options);
        }
      } catch {
        if (mounted) {
          setRuntimeCapacityPoolProfileOptions([]);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [environmentKey, scheduleManager, selectedRuntimeCapacityGrantValue]);

  useEffect(() => {
    if (!selectedSchedulePersistedId && detailTab === DETAIL_TAB_EVENTS) {
      setDetailTab(DETAIL_TAB_SCHEDULE);
    }
  }, [detailTab, selectedSchedulePersistedId]);

  useEffect(() => {
    const normalizedScopeKey = normalizeGuid(initialScopeKey);
    if (!normalizedScopeKey || initialScopeAppliedRef.current === normalizedScopeKey) return;
    const matchedEntry = visibleScheduleEntries.find((entry) =>
      normalizeGuid(getScheduleScopeKey(entry.schedule)) === normalizedScopeKey
    );
    if (!matchedEntry) return;

    initialScopeAppliedRef.current = normalizedScopeKey;
    setSelectedScheduleKey(matchedEntry.key);
    setEditingScheduleKey("");
    editOriginalScheduleRef.current = null;
  }, [initialScopeKey, visibleScheduleEntries]);

  useEffect(() => {
    const selectedStillVisible = visibleScheduleEntries.some((entry) => entry.key === selectedScheduleKey);
    if (selectedStillVisible) return;
    const normalizedInitialScopeKey = normalizeGuid(initialScopeKey);
    const pendingInitialScopeMatch = normalizedInitialScopeKey
      && initialScopeAppliedRef.current !== normalizedInitialScopeKey
      && visibleScheduleEntries.some((entry) => normalizeGuid(getScheduleScopeKey(entry.schedule)) === normalizedInitialScopeKey);
    if (pendingInitialScopeMatch) return;
    setSelectedScheduleKey(visibleScheduleEntries[0]?.key || "");
    setEditingScheduleKey("");
    editOriginalScheduleRef.current = null;
  }, [initialScopeKey, selectedScheduleKey, visibleScheduleEntries]);

  const updateSchedules = useCallback((updater) => {
    setSchedules((previous) => {
      const next = typeof updater === "function" ? updater(previous) : updater;
      onDirty?.();
      return next;
    });
    setActionMessage("");
  }, [onDirty]);

  const addSchedule = useCallback(() => {
    if (!destination || !selectedDeploymentId) {
      const message = "Select a deployment before adding a schedule.";
      setLocalError(message);
      return dialogFailure(message);
    }
    const deployment = asArray(deployments).find((row) => normalizeGuid(getDeploymentRecordId(row)) === normalizeGuid(selectedDeploymentId));
    if (!deployment) {
      const message = "The selected deployment could not be found.";
      setLocalError(message);
      return dialogFailure(message);
    }
    if (addRequiresEntitySelection) {
      const message = "Select one or more entity objects before adding another schedule for this deployment.";
      setLocalError(message);
      return dialogFailure(message);
    }
    const scopeKey = buildDestinationScheduleScopeKey(selectedDeploymentId, selectedEntityObjectIds);
    if (schedulesRef.current.some((schedule) => isScheduleActive(schedule) && getScheduleScopeKey(schedule) === scopeKey)) {
      const message = "A schedule already exists for the selected deployment and entity scope.";
      setLocalError(message);
      return dialogFailure(message);
    }
    const nextScheduleNumber = schedulesRef.current.filter(isScheduleActive).length + 1;
    const nextSchedule = buildDestinationScheduleDraft({
      destination,
      deployment,
      entityObjects,
      entityObjectIds: selectedEntityObjectIds,
      defaults,
      clientResourceId,
      resourceConfigurationId,
      scheduleName: `Schedule ${nextScheduleNumber}`
    });
    const nextScheduleKey = getScheduleRowKey(nextSchedule, schedulesRef.current.length);
    updateSchedules((previous) => [...previous, nextSchedule]);
    setSelectedScheduleKey(nextScheduleKey);
    if (modeRef.current === "management") {
      editOriginalScheduleRef.current = { ...nextSchedule };
      setEditingScheduleKey(nextScheduleKey);
    }
    setSelectedEntityObjectIds([]);
    setAddScheduleDialogOpen(false);
    return { Success: true };
  }, [addRequiresEntitySelection, clientResourceId, defaults, deployments, destination, entityObjects, resourceConfigurationId, selectedDeploymentId, selectedEntityObjectIds, updateSchedules]);

  const updateSelectedSchedule = useCallback((nextSchedule) => {
    updateSchedules((previous) => previous.map((schedule, index) =>
      getScheduleRowKey(schedule, index) === selectedScheduleKey ? nextSchedule : schedule
    ));
  }, [selectedScheduleKey, updateSchedules]);

  const removeSchedule = useCallback((scheduleKey) => {
    updateSchedules((previous) => previous.map((schedule, index) =>
      getScheduleRowKey(schedule, index) === scheduleKey
        ? { ...schedule, IsDeleted: true, IsActive: false }
        : schedule
    ));
    if (selectedScheduleKey === scheduleKey) {
      setSelectedScheduleKey("");
      setEditingScheduleKey("");
      editOriginalScheduleRef.current = null;
    }
  }, [selectedScheduleKey, updateSchedules]);

  const openRemoveScheduleDialog = useCallback((scheduleKey) => {
    setRemoveScheduleKey(scheduleKey);
  }, []);

  const closeRemoveScheduleDialog = useCallback(() => {
    setRemoveScheduleKey("");
  }, []);

  const confirmRemoveSchedule = useCallback(() => {
    if (removeScheduleKey) {
      removeSchedule(removeScheduleKey);
    }
    setRemoveScheduleKey("");
  }, [removeSchedule, removeScheduleKey]);

  const saveSchedules = useCallback(async (decisionOverride = SCHEDULE_DECISION_NONE) => {
    const activeManager = scheduleManagerRef.current;
    const decision = resolveScheduleDecision(schedulesRef.current, decisionOverride || SCHEDULE_DECISION_NONE);
    const activeSchedules = decision === SCHEDULE_DECISION_NONE
      ? []
      : schedulesRef.current.filter(isScheduleActive);
    const schedulesToDelete = decision === SCHEDULE_DECISION_NONE
      ? schedulesRef.current.filter((schedule) => getPersistedScheduleId(schedule))
      : schedulesRef.current.filter((schedule) => getPersistedScheduleId(schedule) && readValue(schedule, "IsDeleted", "isDeleted") === true);
    if (decision !== SCHEDULE_DECISION_NONE) {
      const validationMessage = validateDestinationSchedules(activeSchedules);
      if (validationMessage) {
        setLocalError(validationMessage);
        throw new Error(validationMessage);
      }
    }

    if ((decision !== SCHEDULE_DECISION_NONE || schedulesToDelete.length > 0) && !activeManager?.saveItem) {
      const message = "Destination Schedule service is not available.";
      setLocalError(message);
      throw new Error(message);
    }

    setBusyAction("save");
    setLocalError("");
    setActionMessage("");
    try {
      const savedSchedules = [];
      const currentSelectedSchedule = schedulesRef.current.find((schedule, index) => getScheduleRowKey(schedule, index) === selectedScheduleKey);
      const currentSelectedScopeKey = currentSelectedSchedule ? getScheduleScopeKey(currentSelectedSchedule) : "";
      for (const schedule of schedulesToDelete) {
        const deletePayload = {
          ...schedule,
          IsNew: false,
          IsActive: false,
          IsDeleted: true,
          EnableSchedule: false
        };
        const response = await saveScheduleItem(activeManager, deletePayload);
        requireSuccessfulServiceResponse(response, "Destination Schedule could not be removed.");
      }
      for (const schedule of activeSchedules) {
        const payload = {
          ...schedule,
          _tempKey: undefined,
          ClientResourceId: clientResourceId,
          ResourceConfigurationId: resourceConfigurationId,
          SchemaDestinationId: destinationId,
          IsNew: !getPersistedScheduleId(schedule),
          EnableSchedule: modeRef.current === "setup" ? false : readValue(schedule, "EnableSchedule", "enableSchedule") === true,
          ScopeKey: getScheduleScopeKey(schedule)
        };
        const response = await saveScheduleItem(activeManager, payload);
        requireSuccessfulServiceResponse(response, "Destination Schedule could not be saved.");
        savedSchedules.push(getOperationItem(response) || payload);
      }
      const stepValues = buildScheduleStepValues(savedSchedules, decision);
      savedSchedulesRef.current = savedSchedules;
      setSchedules(decision === SCHEDULE_DECISION_NONE ? [] : savedSchedules);
      const nextSelectedSchedule = savedSchedules.find((schedule) => getScheduleScopeKey(schedule) === currentSelectedScopeKey)
        || savedSchedules[0]
        || null;
      lastSavedSelectedScheduleRef.current = nextSelectedSchedule;
      setSelectedScheduleKey(nextSelectedSchedule ? getScheduleRowKey(nextSelectedSchedule, savedSchedules.indexOf(nextSelectedSchedule)) : "");
      setActionMessage(decision === SCHEDULE_DECISION_NONE ? "Schedule skipped for setup." : "Schedule templates saved.");
      onSaved?.(stepValues, savedSchedules);
      return stepValues;
    } catch (ex) {
      setLocalError(ex?.message || "Destination Schedule could not be saved.");
      throw ex;
    } finally {
      setBusyAction("");
    }
  }, [clientResourceId, destinationId, onSaved, resourceConfigurationId, selectedScheduleKey]);

  useEffect(() => {
    const api = {
      saveSchedules,
      getStepValues: (decision = SCHEDULE_DECISION_NONE) => buildScheduleStepValues(schedulesRef.current, decision)
    };
    onApiReady?.(api);
    return () => onApiReady?.(null);
  }, [onApiReady, saveSchedules]);

  const runScheduleOperation = useCallback(async (operationName, actionKey, fallbackMessage, options = {}) => {
    const schedule = options.schedule || selectedSchedule;
    if (!schedule || !operationName) return;
    const operation = getScheduleOperationDefinition(operationName);
    if (!operation) {
      setLocalError(`Destination Schedule operation metadata '${operationName}' was not found.`);
      return;
    }
    if (!readValue(operation, "Name")) {
      setLocalError(`Destination Schedule operation metadata '${operationName}' is missing its operation Name.`);
      return;
    }
    if (!scheduleOperationDialog?.handleOperationDialog) {
      setLocalError("Destination Schedule operation dialog service is not available.");
      return;
    }
    setBusyAction(actionKey);
    setLocalError("");
    if (!options.preserveActionMessage) {
      setActionMessage("");
    }
    try {
      const baseQueryParams = buildResourceOperationQueryParams({
        clientResourceId,
        resourceConfigurationId,
        item: schedule
      });
      await scheduleOperationDialog.handleOperationDialog(
        {
          ...operation,
          DisplayName: options.displayName || operation.DisplayName,
          Details: options.details || operation.Details,
          itemData: schedule,
          queryParams: {
            ...baseQueryParams,
            ...(options.queryParams || {})
          }
        },
        {
          closeOnComplete: false,
          resultsCallback: async (response) => {
            if (operationFailed(response)) {
              throw new Error(normalizeResultMessage(response, fallbackMessage));
            }
            const updatedSchedule = getOperationItem(response);
            if (updatedSchedule) {
              savedSchedulesRef.current = schedulesRef.current.map((scheduleItem) =>
                getScheduleRowKey(scheduleItem, 0) === getScheduleRowKey(updatedSchedule, 0)
                  || normalizeGuid(getPersistedScheduleId(scheduleItem)) === normalizeGuid(getPersistedScheduleId(updatedSchedule))
                  ? updatedSchedule
                  : scheduleItem
              );
              updateSelectedSchedule(updatedSchedule);
            }
            if (options.successMessage) {
              setActionMessage(options.successMessage);
            }
          }
        }
      );
    } catch (ex) {
      setLocalError(ex?.message || fallbackMessage);
    } finally {
      setBusyAction("");
    }
  }, [clientResourceId, getScheduleOperationDefinition, resourceConfigurationId, scheduleOperationDialog, selectedSchedule, updateSelectedSchedule]);

  const canEdit = !readOnly && !saving && !loading && !busyAction;
  const handleManagementSave = useCallback(() =>
    saveSchedules(SCHEDULE_DECISION_CONFIGURE), [saveSchedules]);
  const handleEditorSave = useCallback(async () => {
    try {
      await handleManagementSave();
      setEditingScheduleKey("");
      editOriginalScheduleRef.current = null;
    } catch {
      // saveSchedules owns the visible error; keep the editor open so it can be corrected.
    }
  }, [handleManagementSave]);
  const handlePreview = useCallback(() =>
    runScheduleOperation(scheduleOperations.PreviewDestinationScheduleEvent, "preview", "Schedule preview completed."), [runScheduleOperation]);
  const handleApply = useCallback(async () => {
    let scheduleToApply = selectedSchedule;
    let savedBeforeApply = false;
    if (selectedScheduleHasUnsavedChanges) {
      await handleManagementSave();
      savedBeforeApply = true;
      scheduleToApply = lastSavedSelectedScheduleRef.current || findSavedScheduleMatch(savedSchedulesRef.current, selectedSchedule) || selectedSchedule;
    }
    await runScheduleOperation(scheduleOperations.ApplyDestinationSchedule, "apply", "Schedule applied.", {
      schedule: scheduleToApply,
      preserveActionMessage: savedBeforeApply,
      successMessage: savedBeforeApply ? "Schedule templates saved. Schedule applied." : "Schedule applied."
    });
  }, [handleManagementSave, runScheduleOperation, selectedSchedule, selectedScheduleHasUnsavedChanges]);
  const handleRunNow = useCallback(() =>
    runScheduleOperation(scheduleOperations.RunDestinationScheduleNow, "run-now", "Run Now started."), [runScheduleOperation]);
  const handleRuntimeCapacityPurchased = useCallback((clientProductOffer, result, selectedOffer) => {
    if (!selectedSchedule) return;
    const resultValue = result?.Value || result?.value || {};
    const capacityGrant = (selectedOffer?.capacityGrants || [])[0] || {};
    const clientProductOfferId = resultValue.clientProductOfferId
      || clientProductOffer?._id
      || clientProductOffer?.id
      || clientProductOffer?.UniqueId
      || "";
    const capacityGrantKey = resultValue.capacityGrantKey
      || capacityGrant.capacityGrantKey
      || selectedOffer?.key
      || "";
    const displayName = resultValue.displayName
      || capacityGrant.displayName
      || selectedOffer?.name
      || "";
    const selectedValue = resultValue.selectedValue
      || (clientProductOfferId && capacityGrantKey ? `${clientProductOfferId}|${capacityGrantKey}` : "");

    if (!selectedValue) return;

    updateSelectedSchedule({
      ...selectedSchedule,
      RuntimeCapacityClientProductOfferId: selectedValue,
      RuntimeCapacityGrantKey: capacityGrantKey,
      RuntimeCapacityGrantDisplayName: displayName,
      RuntimeCapacityProductOfferKey: "",
      RuntimeCapacityProductOfferSku: "",
      RuntimeCapacityPoolType: null,
      RuntimeCapacityIsUnlimited: false,
      RuntimeCapacityPoolProfileId: "",
      PoolProfileId: ""
    });
  }, [selectedSchedule, updateSelectedSchedule]);
  const openAddScheduleDialog = useCallback(() => {
    if (selectedScheduleBlocksAdd) return;
    setLocalError("");
    setActionMessage("");
    if (linkedSpecificEntityScope) {
      setSelectedEntityObjectIds(initialFilterEntityObjectIds);
    }
    setAddScheduleDialogOpen(true);
  }, [initialFilterEntityObjectIds, linkedSpecificEntityScope, selectedScheduleBlocksAdd]);
  const closeAddScheduleDialog = useCallback(() => {
    setAddScheduleDialogOpen(false);
  }, []);
  const selectSchedule = useCallback((scheduleKey) => {
    setSelectedScheduleKey(scheduleKey);
    setEditingScheduleKey("");
    editOriginalScheduleRef.current = null;
  }, []);
  const startEditSelectedSchedule = useCallback(() => {
    if (!selectedSchedule || !selectedScheduleKey) return;
    editOriginalScheduleRef.current = { ...selectedSchedule };
    setEditingScheduleKey(selectedScheduleKey);
  }, [selectedSchedule, selectedScheduleKey]);
  const cancelEditSelectedSchedule = useCallback(() => {
    const originalSchedule = editOriginalScheduleRef.current;
    if (originalSchedule) {
      updateSelectedSchedule(originalSchedule);
    }
    editOriginalScheduleRef.current = null;
    setEditingScheduleKey("");
  }, [updateSelectedSchedule]);
  const selectedScheduleEditing = managementMode
    ? Boolean(selectedSchedule)
    : true;
  const selectedScheduleEnabled = readValue(selectedSchedule, "EnableSchedule", "enableSchedule") === true;
  const scheduleActionDisabled = !selectedSchedulePersistedId || !canEdit;
  const applyScheduleDisabled = scheduleActionDisabled || !selectedScheduleEnabled;
  const applyScheduleTitle = !selectedSchedulePersistedId
    ? "Save and select a schedule before applying it."
    : !selectedScheduleEnabled
      ? "Turn on Enabled before applying this schedule."
      : !canEdit
        ? "Wait for the current action to finish before applying this schedule."
        : "Create or update the active scheduled event for this schedule.";
  const templateActions = useMemo(() => {
    if (!managementMode) return null;
    return [
      {
        key: "destination-schedule-preview",
        label: "Preview Event",
        iconKey: "preview",
        disabled: scheduleActionDisabled,
        loading: busyAction === "preview",
        title: selectedSchedulePersistedId ? "Preview the event that would be generated for this schedule without creating or running it." : "Save and select a schedule before previewing its event.",
        onClick: handlePreview
      },
      {
        key: "destination-schedule-apply",
        label: "Apply Schedule",
        iconKey: "success",
        disabled: applyScheduleDisabled,
        loading: busyAction === "apply",
        title: applyScheduleTitle,
        onClick: handleApply
      },
      {
        key: "destination-schedule-run-now",
        label: "Run Now",
        iconKey: "play",
        type: "ok-small",
        disabled: scheduleActionDisabled,
        loading: busyAction === "run-now",
        title: selectedSchedulePersistedId ? "Run this selected schedule once in the background now." : "Save and select a schedule before running it now.",
        onClick: handleRunNow
      }
    ];
  }, [applyScheduleDisabled, applyScheduleTitle, busyAction, canEdit, handleApply, handlePreview, handleRunNow, managementMode, scheduleActionDisabled, selectedSchedulePersistedId]);

  const managementActions = useMemo(() => {
    if (!managementMode) return null;
    const selectedActionDisabled = !selectedSchedule || !canEdit;
    return {
      busyAction,
      selectedScheduleId: selectedSchedulePersistedId,
      selectedScheduleName: readValue(selectedSchedule, "Name", "name") || "",
      actions: [
        {
          key: "destination-schedule-runtime-capacity-grant",
          label: `${runtimeCapacityActionVerb} Grant`,
          iconKey: "add",
          disabled: selectedActionDisabled,
          title: selectedSchedule ? `${runtimeCapacityActionVerb} a runtime capacity grant for this schedule.` : "Select or add a schedule before choosing runtime capacity.",
          onClick: () => setRuntimeCapacityPurchaseOpen(true)
        },
        {
          key: "destination-schedule-save",
          label: busyAction === "save" ? "Saving..." : "Save",
          iconKey: "save",
          disabled: selectedActionDisabled,
          loading: busyAction === "save",
          title: selectedSchedule ? "Save the selected schedule." : "Select or add a schedule to save.",
          onClick: handleManagementSave
        },
        {
          key: "destination-schedule-preview",
          label: "Preview Event",
          iconKey: "preview",
          disabled: scheduleActionDisabled,
          loading: busyAction === "preview",
          title: selectedSchedulePersistedId ? "Preview the event that would be generated for this schedule without creating or running it." : "Save and select a schedule before previewing its event.",
          onClick: handlePreview
        },
        {
          key: "destination-schedule-apply",
          label: "Apply Schedule",
          iconKey: "success",
          disabled: applyScheduleDisabled,
          loading: busyAction === "apply",
          title: applyScheduleTitle,
          onClick: handleApply
        },
        {
          key: "destination-schedule-run-now",
          label: "Run Now",
          iconKey: "play",
          disabled: scheduleActionDisabled,
          loading: busyAction === "run-now",
          title: selectedSchedulePersistedId ? "Run this selected schedule once in the background now." : "Save and select a schedule before running it now.",
          onClick: handleRunNow
        }
      ]
    };
  }, [applyScheduleDisabled, applyScheduleTitle, busyAction, canEdit, handleApply, handleManagementSave, handlePreview, handleRunNow, managementMode, runtimeCapacityActionVerb, scheduleActionDisabled, selectedSchedule, selectedSchedulePersistedId]);

  useEffect(() => {
    onManagementActionsChange?.(managementActions);
  }, [managementActions, onManagementActionsChange]);

  useEffect(() => () => {
    onManagementActionsChange?.(null);
  }, [onManagementActionsChange]);

  const showDetailTabs = managementMode && Boolean(selectedSchedule);
  const activeDetailTab = showDetailTabs ? detailTab : DETAIL_TAB_SCHEDULE;
  const detailTabIdPrefix = `destination-schedule-detail-${String(selectedScheduleKey || "none").replace(/[^a-z0-9_-]/gi, "-")}`;
  const scheduleDetailPanel = (
    <div className="destination-schedule-control__editor-shell">
      <ScheduleEditorControl
        schedule={selectedSchedule}
        defaults={defaults}
        mode={mode}
        disabled={!canEdit}
        timezoneOptions={timezoneOptions}
        runtimeCapacityPoolProfileOptions={runtimeCapacityPoolProfileOptions}
        templateActions={managementMode ? [] : templateActions || []}
        isEditing={selectedScheduleEditing}
        saving={busyAction === "save"}
        onEdit={startEditSelectedSchedule}
        onSave={handleEditorSave}
        onCancelEdit={cancelEditSelectedSchedule}
        onChange={updateSelectedSchedule}
        showManagementEditorActions={!managementMode}
      />
    </div>
  );
  const eventsDetailPanel = managementMode && selectedSchedulePersistedId ? (
    <DestinationScheduleStatusSection
      schedule={selectedSchedule}
      clientResourceId={clientResourceId}
      resourceConfigurationId={resourceConfigurationId}
      eventManager={eventManager}
    />
  ) : null;
  const pendingRemoveSchedule = removeScheduleKey
    ? schedules.find((schedule, index) => getScheduleRowKey(schedule, index) === removeScheduleKey)
    : null;
  const pendingRemoveScheduleTitle = pendingRemoveSchedule
    ? readValue(pendingRemoveSchedule, "Name", "name") || readValue(pendingRemoveSchedule, "ScopeDisplayName", "scopeDisplayName") || "this schedule"
    : "this schedule";
  const scheduleOperationDialogControl = (
    <DialogControl
      controlOrigin="destination-schedule-execute-operation"
      openDialog={scheduleOperationDialog?.openDialog}
      title={scheduleOperationDialog?.activeOperation?.DisplayName ?? scheduleOperationDialog?.activeOperation?.Name ?? "Operation Completed"}
      subTitle={scheduleOperationDialog?.activeOperation?.Details}
      onCancel={scheduleOperationDialog?.cancelOperation}
      onOk={async () => scheduleOperationDialog?.executeOperation?.()}
      excludeCancel={!scheduleOperationDialog?.activeOperation}
      okText={(scheduleOperationDialog?.activeOperation && scheduleOperationDialog?.operationErrors) ? "Retry" : "OK"}
      disableCancel={scheduleOperationDialog?.operationSuccessMessage}
    >
      {scheduleOperationDialog?.renderOperationContent?.()}
    </DialogControl>
  );
  const runtimeCapacityPurchaseDialogControl = (
    <MarketplaceRuntimeCapacityPurchaseDialog
      open={runtimeCapacityPurchaseOpen}
      product={{ name: "Processing Capacity" }}
      environmentKey={environmentKey}
      clientProductOffers={clientProductOffers}
      isSelfHostedResourceMode={isSelfHostedResourceMode}
      actionVerb={runtimeCapacityActionVerb}
      beforeCheckout={managementMode ? handleManagementSave : onSetupSave}
      onClose={() => setRuntimeCapacityPurchaseOpen(false)}
      onPurchased={handleRuntimeCapacityPurchased}
    />
  );

  if (managementMode) {
    const addDialogContent = (
      <div className="destination-schedule-control__add-dialog destination-schedule-control__add-dialog--cards">
        <p>
          Deployment is fixed from the link that opened Schedule. Select entity objects for a scoped schedule, or leave the selection empty to schedule all entities.
        </p>
        {linkedSpecificEntityScope ? (
          <div className="destination-schedule-control__linked-scope-note">This Schedule link is scoped to the selected Entity Scope row.</div>
        ) : null}
        {addRequiresEntitySelection ? (
          <div className="resource-wizard__inline-error">Select one or more entity objects before adding another schedule for this deployment.</div>
        ) : null}
        <CardGridControl
          className="destination-schedule-control__entity-card-selector"
          rows={addDialogEntityRows}
          metadata={ENTITY_OBJECT_SELECTOR_METADATA}
          cardFields={ENTITY_OBJECT_SELECTOR_METADATA.Fields.filter((field) => field.ShowOnCard)}
          rowKey={(row) => row._id}
          selectable
          multiSelect
          selectedRowKeys={selectedEntityObjectIds}
          onSelectedRowKeysChange={(keys) => setSelectedEntityObjectIds(normalizeStringArray(keys))}
          readOnly={linkedSpecificEntityScope}
          disabled={!canEdit}
          activeFilterEnabled={false}
          searchable={addDialogEntityRows.length > 1}
          emptyContent={linkedSpecificEntityScope ? "The linked Entity Scope row is not available." : "No unscheduled entity objects are available for this deployment."}
          minCardWidth="12rem"
          maxColumns={2}
        />
      </div>
    );
    const removeDialog = (
      <DialogControl
        title="Remove Schedule"
        openDialog={Boolean(removeScheduleKey)}
        okText="Remove"
        cancelText="Cancel"
        onOk={confirmRemoveSchedule}
        onCancel={closeRemoveScheduleDialog}
        onX={closeRemoveScheduleDialog}
        disableOk={!canEdit}
        disableCancel={Boolean(busyAction)}
      >
        <div className="destination-schedule-control__add-dialog">
          <p>Remove {pendingRemoveScheduleTitle}? This schedule template will be removed from the list.</p>
        </div>
      </DialogControl>
    );
    const leftPane = (
      <ResourceCatalogListControl
        className="destination-schedule-control__catalog-list"
        summary={`${visibleSchedules.length.toLocaleString()} schedule${visibleSchedules.length === 1 ? "" : "s"}`}
        items={visibleScheduleEntries}
        getItemKey={(entry) => entry.key}
        ariaLabel="Destination schedules"
        emptyMessage="No schedules have been added for this linked scope."
        emptyFilteredMessage="No schedules match the current search."
        search={{
          fieldMetadata: buildFilterTextBoxField("DestinationScheduleSearch", "Search schedules...", "Search schedules"),
          value: scheduleSearch,
          onChange: (nextValue) => setScheduleSearch(nextValue || ""),
          matchesItem: ({ schedule }, value) => {
            const normalizedValue = normalizeGuid(value);
            if (!normalizedValue) return true;
            return [
              readValue(schedule, "Name", "name"),
              readValue(schedule, "ScopeDisplayName", "scopeDisplayName"),
              getScheduleScopeKey(schedule),
              buildDestinationScheduleTimingSummary(schedule)
            ].some((entry) => normalizeGuid(entry).includes(normalizedValue));
          }
        }}
        actions={(
          <ButtonControl
            type="standard-small"
            title={selectedScheduleBlocksAdd ? "Save the current schedule before adding another schedule." : "Add a schedule for this linked deployment and scope."}
            aria-label="Add Schedule"
            onClick={openAddScheduleDialog}
            disabled={!canEdit || !selectedDeploymentId || selectedScheduleBlocksAdd}
          >
            <IconControl iconKey="add" title="Add Schedule" width="1rem" height="1rem" />
          </ButtonControl>
        )}
        renderItem={({ schedule, key }) => {
          const selected = key === selectedScheduleKey;
          const scheduleScope = readValue(schedule, "ScopeDisplayName", "scopeDisplayName") || getScheduleScopeKey(schedule);
          const scheduleTitle = readValue(schedule, "Name", "name") || scheduleScope || "Destination Schedule";
          const scheduleSummary = buildDestinationScheduleTimingSummary(schedule);
          const scheduleDeploymentName = deploymentDisplayById.get(getScheduleDeploymentKey(schedule)) || "";
          return (
            <ResourceCatalogRow
              title={scheduleTitle}
              subtitle={scheduleScope}
              titleAttribute={scheduleTitle}
              className={`destination-schedule-control__catalog-row${selected ? " resource-catalog-row--selected" : ""}`}
              ariaLabel={`Select schedule ${scheduleTitle}`}
              onClick={() => selectSchedule(key)}
              metaItems={[
                { key: "timing", className: "resource-catalog-row__meta-item--detail", content: scheduleSummary },
                scheduleDeploymentName ? { key: "deployment", className: "resource-catalog-row__meta-item--detail", content: scheduleDeploymentName } : null
              ].filter(Boolean)}
              actions={(
                <span
                  className="destination-schedule-control__catalog-row-delete"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <ButtonControl
                    type="standard-small"
                    title={`Remove ${scheduleTitle}`}
                    aria-label={`Remove ${scheduleTitle}`}
                    disabled={!canEdit}
                    onClick={(event) => {
                      event?.stopPropagation?.();
                      openRemoveScheduleDialog(key);
                    }}
                  >
                    <IconControl iconKey="delete" title={`Remove ${scheduleTitle}`} width="0.95rem" height="0.95rem" />
                  </ButtonControl>
                </span>
              )}
            />
          );
        }}
      />
    );
    const middlePane = selectedSchedule ? (
      <div className="destination-schedule-control__management-editor">
        {selectedSchedulePersistedId ? (
          <TabPaneControl
            className="destination-schedule-control__detail-tabs"
            tabs={[
              { key: DETAIL_TAB_SCHEDULE, title: "Schedule", content: scheduleDetailPanel },
              { key: DETAIL_TAB_EVENTS, title: "Events", content: eventsDetailPanel }
            ]}
            activeKey={activeDetailTab}
            onChange={(index, tab, key) => setDetailTab(key || tab?.key || DETAIL_TABS[index]?.key || DETAIL_TAB_SCHEDULE)}
            idPrefix={detailTabIdPrefix}
            compact
            framed={false}
            align="right"
            unmountInactive
          />
        ) : scheduleDetailPanel}
      </div>
    ) : (
      <div className="destination-schedule-control__management-editor">
        {scheduleDetailPanel}
      </div>
    );

    return (
      <div className="destination-schedule-control destination-schedule-control--management">
        {localError ? <div className="resource-wizard__inline-error" role="alert">{localError}</div> : null}
        {actionMessage ? <div className="destination-schedule-control__message">{actionMessage}</div> : null}
        {loading ? (
          <div className="resource-wizard__linked-step-loading"><Loading /></div>
        ) : (
          <>
            <AIPanel
              open
              presentation="inline"
              title="Schedule"
              className="destination-schedule-control__designer"
              chatEnabled={false}
              chatPaneToggleEnabled={false}
              chatPane="right"
              chatPaneCollapseMode="closed"
              chatPaneStartOpen={false}
              splitPaneStorageKey={`destination-schedule:no-operation:${linkedScopeDeploymentId || destinationId || "default"}`}
              initialRightPct={0.76}
              enableContentDragHiding={false}
              minLeft={280}
              minRight={460}
              left={leftPane}
              right={middlePane}
            />
            <DialogControl
              title="Add Schedule"
              openDialog={addScheduleDialogOpen}
              okText="Add Schedule"
              cancelText="Cancel"
              onOk={addSchedule}
              onCancel={closeAddScheduleDialog}
              onX={closeAddScheduleDialog}
              disableOk={!canEdit || !selectedDeploymentId || addRequiresEntitySelection}
              disableCancel={Boolean(busyAction)}
            >
              {addDialogContent}
            </DialogControl>
            {removeDialog}
            {scheduleOperationDialogControl}
            {runtimeCapacityPurchaseDialogControl}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="destination-schedule-control">
      {localError ? <div className="resource-wizard__inline-error" role="alert">{localError}</div> : null}
      {actionMessage ? <div className="destination-schedule-control__message">{actionMessage}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : (
        <>
          {mode === "setup" ? (
            <div className="destination-schedule-control__add-panel">
              <div>
                <h4>Schedule Sets</h4>
                <span>Add a deployment and optional entity scope for each schedule template.</span>
              </div>
              <ButtonControl
                type="ok-small"
                className="destination-schedule-control__add-button"
                onClick={openAddScheduleDialog}
                disabled={!canEdit || !selectedDeploymentId}
              >
                <span className="destination-schedule-control__button-icon" aria-hidden="true">
                  <IconControl iconKey="add" width="1rem" height="1rem" />
                </span>
                <span>Add Schedule</span>
              </ButtonControl>
            </div>
          ) : null}

          <DialogControl
            title="Add Schedule"
            openDialog={addScheduleDialogOpen}
            okText="Add Schedule"
            cancelText="Cancel"
            onOk={addSchedule}
            onCancel={closeAddScheduleDialog}
            onX={closeAddScheduleDialog}
            disableOk={!canEdit || !selectedDeploymentId || addRequiresEntitySelection}
            disableCancel={Boolean(busyAction)}
          >
            <div className="destination-schedule-control__add-dialog">
              <p>One deployment plus selected entity objects. Leave entity objects empty to schedule all entities for the deployment.</p>
              {addRequiresEntitySelection ? (
                <div className="resource-wizard__inline-error">Select one or more entity objects before adding another schedule for this deployment.</div>
              ) : null}
              <div className="destination-schedule-control__field-grid destination-schedule-control__field-grid--scope">
                <div className="destination-schedule-control__field">
                  <FieldControl
                    className="destination-schedule-control__field-control"
                    fieldMetadata={fieldMetadata({ fieldName: "ScheduleDeployment", displayName: "Deployment", fieldType: SystemTypes.UIFieldType.SelectList, options: deploymentOptions, isRequired: true })}
                    selectData={deploymentOptions}
                    value={selectedDeploymentId}
                    disabled={!canEdit}
                    groupId={`${DESTINATION_SCHEDULE_FORM_GROUP_ID}-scope-deployment`}
                    onChange={(nextValue) => {
                      setSelectedDeploymentId(fieldValue(nextValue));
                      setSelectedEntityObjectIds([]);
                    }}
                    disableError
                  />
                </div>
                <div className="destination-schedule-control__field">
                  <FieldControl
                    className="destination-schedule-control__field-control"
                    fieldMetadata={fieldMetadata({ fieldName: "ScheduleEntityObjects", displayName: "Entity Objects", fieldType: SystemTypes.UIFieldType.MultiSelectList, options: entityOptions })}
                    selectData={entityOptions}
                    value={selectedEntityObjectIds}
                    disabled={!canEdit}
                    groupId={`${DESTINATION_SCHEDULE_FORM_GROUP_ID}-scope-entities`}
                    onChange={(nextValue) => setSelectedEntityObjectIds(normalizeStringArray(nextValue))}
                    disableError
                  />
                </div>
              </div>
            </div>
          </DialogControl>

          <DialogControl
            title="Remove Schedule"
            openDialog={Boolean(removeScheduleKey)}
            okText="Remove"
            cancelText="Cancel"
            onOk={confirmRemoveSchedule}
            onCancel={closeRemoveScheduleDialog}
            onX={closeRemoveScheduleDialog}
            disableOk={!canEdit}
            disableCancel={Boolean(busyAction)}
          >
            <div className="destination-schedule-control__add-dialog">
              <p>Remove {pendingRemoveScheduleTitle}? This schedule template will be removed from the list.</p>
            </div>
          </DialogControl>

          <div className="destination-schedule-control__layout">
            <div className="destination-schedule-control__list">
              <div className="destination-schedule-control__section-heading">
                <h4>Schedules</h4>
                <span>{visibleSchedules.length} shown</span>
              </div>
              <div className="destination-schedule-control__list-filter">
                <FieldControl
                  className="destination-schedule-control__field-control"
                  fieldMetadata={{
                    ...fieldMetadata({ fieldName: "ScheduleFilterDeployment", displayName: "", fieldType: SystemTypes.UIFieldType.SelectList, options: deploymentFilterOptions }),
                    AriaLabel: "Deployment"
                  }}
                  selectData={deploymentFilterOptions}
                  value={selectedDeploymentId}
                  disabled={loading || !deploymentOptions.length}
                  groupId={`${DESTINATION_SCHEDULE_FORM_GROUP_ID}-list-deployment`}
                  onChange={(nextValue) => {
                    setSelectedDeploymentId(fieldValue(nextValue));
                    setSelectedEntityObjectIds([]);
                    setSelectedScheduleKey("");
                    setEditingScheduleKey("");
                    editOriginalScheduleRef.current = null;
                  }}
                  disableError
                />
              </div>
              {visibleScheduleEntries.map(({ schedule, key }) => {
                const selected = key === selectedScheduleKey;
                const scheduleScope = readValue(schedule, "ScopeDisplayName", "scopeDisplayName") || getScheduleScopeKey(schedule);
                const scheduleTitle = readValue(schedule, "Name", "name") || scheduleScope || "Destination Schedule";
                const scheduleSummary = buildDestinationScheduleTimingSummary(schedule);
                const scheduleDeploymentName = deploymentDisplayById.get(getScheduleDeploymentKey(schedule)) || "";
                return (
                  <div
                    className={`destination-schedule-control__card${selected ? " destination-schedule-control__card--selected" : ""}`}
                    key={key}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select schedule ${scheduleTitle}`}
                    aria-pressed={selected}
                    onClick={() => selectSchedule(key)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectSchedule(key);
                      }
                    }}
                  >
                    <div className="destination-schedule-control__card-content">
                      <strong>{scheduleTitle}</strong>
                      {scheduleDeploymentName ? <span>{scheduleDeploymentName}</span> : null}
                      <span>{scheduleSummary}</span>
                    </div>
                    <div className="destination-schedule-control__card-actions">
                      <button
                        type="button"
                        className="destination-schedule-control__icon-button destination-schedule-control__icon-button--danger"
                        title="Remove"
                        aria-label={`Remove ${scheduleTitle}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          openRemoveScheduleDialog(key);
                        }}
                        disabled={!canEdit}
                      >
                        <IconControl
                          className="destination-schedule-control__delete-icon"
                          iconKey="delete"
                          width="1.5rem"
                          height="1.5rem"
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="destination-schedule-control__detail">
              {showDetailTabs ? (
                <div className="form-wrapper form-wrapper-tabs-top destination-schedule-control__detail-tabs">
                  <div className="form-tabs-container form-tabs-container-top destination-schedule-control__detail-tab-list" role="tablist" aria-label="Schedule detail sections">
                    {DETAIL_TABS.map((tab) => {
                      const active = activeDetailTab === tab.key;
                      return (
                        <button
                          key={tab.key}
                          id={`${detailTabIdPrefix}-${tab.key}-tab`}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          aria-controls={`${detailTabIdPrefix}-${tab.key}-panel`}
                          className={`form-tab-rows destination-schedule-control__detail-tab ${active ? "form-tab-row-active destination-schedule-control__detail-tab--active" : ""}`}
                          onClick={() => setDetailTab(tab.key)}
                        >
                          <span className="form-tab">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div
                    id={`${detailTabIdPrefix}-${activeDetailTab}-panel`}
                    role="tabpanel"
                    aria-labelledby={`${detailTabIdPrefix}-${activeDetailTab}-tab`}
                    className="destination-schedule-control__detail-tab-panel"
                  >
                    {activeDetailTab === DETAIL_TAB_EVENTS ? eventsDetailPanel : scheduleDetailPanel}
                  </div>
                </div>
              ) : null}
              {!showDetailTabs ? scheduleDetailPanel : null}
            </div>
          </div>

          {mode === "setup" ? (
            <div className="destination-schedule-control__actions">
              <>
                <ButtonControl type="standard-small" className="resource-wizard__button resource-wizard__button--secondary" onClick={onSetupSave} disabled={readOnly || saving || Boolean(busyAction)} loading={saving || busyAction === "save"}>
                  Save
                </ButtonControl>
                <ButtonControl type="ok-small" className="resource-wizard__button resource-wizard__button--primary" onClick={onSetupContinue} disabled={readOnly || saving || Boolean(busyAction)} loading={saving || busyAction === "save"}>
                  Save & Continue
                </ButtonControl>
                <ButtonControl type="standard-small" className="resource-wizard__button resource-wizard__button--secondary" onClick={onSetupNoSchedule} disabled={readOnly || saving || Boolean(busyAction)} loading={saving || busyAction === "save"}>
                  Skip Scheduling
                </ButtonControl>
              </>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default DestinationScheduleControl;
